import { TaskAssignee, TaskStatus } from "@repo/api/client";
import {
	ActivityType,
	ConsentStatus,
	JobType,
	TaskListFilter,
} from "@repo/db/client";
import {
	Avatar,
	Button,
	Checkbox,
	ColorName,
	Flex,
	Icon,
	IconName,
	Menu,
	MenuItem,
	MenuTrigger,
	Popover,
	Select,
	Spacer,
	Text,
	TextField,
	useTheme,
	WithDialog,
} from "@repo/ui";
import { formatTimestampRelative } from "@repo/utils/dateUtils";
import { keys, sortBy } from "@repo/utils/objectUtils";
import { tag, unreachable } from "@repo/utils/typeUtils";
import { useStyles } from "purse-styles";
import {
	forwardRef,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { Button as AriaButton } from "react-aria-components";
import { toast } from "sonner";
import { useLocation, useRoute } from "wouter";
import { jobTypeMeta } from "../activityLog/jobTypeMeta";
import { useApiClient } from "../contexts/ApiContext";
import { signOutAndReload, useLoggedInUser } from "../contexts/AuthContext";
import {
	Experiment,
	EXPERIMENTS,
	useExperiment,
} from "../contexts/experiments";
import {
	useDatabase,
	useEntity,
	useQuery,
	useTransaction,
} from "../db/DatabaseProvider";
import { Patient, PatientId, Task, TaskId, User } from "../db/schema";
import { RoutePattern, Routes } from "../routing";
import {
	offersSortChoice,
	recommendedSortDescription,
	sentReferralFilterFor,
	sortsByWorkQueue,
	statusForTaskListFilter,
} from "../taskListFilter";
import {
	lastTouchedAt,
	lastTouchedByTask,
	referralSortDate,
	sortTasksByLastTouched,
	sortTasksByReferralDate,
	sortTasksForLeftPane,
	TaskSortMode,
} from "../taskSort";
import { TrainingGuideCard } from "./TrainingGuideCard";

function ExperimentToggle({ experiment }: { experiment: Experiment }) {
	const [isEnabled, setIsEnabled] = useExperiment(experiment);
	const config = EXPERIMENTS[experiment];

	return (
		<Flex align="center" justify="space-between">
			<Flex align="center" gap="xs">
				<Icon name={config.icon} size="sm" color="sand11" />
				<Text size="sm" color="sand12">
					{config.name}
				</Text>
			</Flex>
			<Checkbox
				isSelected={isEnabled}
				onChange={setIsEnabled}
				aria-label={`Toggle ${config.name}`}
			/>
		</Flex>
	);
}

// Get department options from database
const getDepartmentOptions = (departments: { name: string }[]) => {
	// Extract unique department names and add "All Departments" option
	const departmentNames = [...new Set(departments.map((dept) => dept.name))];
	return [...departmentNames, "All Departments"];
};

type StatusViewProps = {
	status: Task["status"];
	unsnoozeDate: Task["unsnoozeDate"];
	runningJobs: Task["runningJobs"];
};

const getStatusConfig = (
	status: Task["status"],
	runningJobs: Task["runningJobs"],
	unsnoozeDate: Task["unsnoozeDate"],
): { icon: IconName; color: ColorName; text: string; shimmer: boolean } => {
	if (runningJobs.length > 0 && runningJobs[0]) {
		const { icon, textColor, label } = jobTypeMeta[runningJobs[0].jobType];
		const text = label ?? "Processing";
		return { icon, color: textColor, text, shimmer: true };
	} else {
		switch (status) {
			case TaskStatus.ActionRequired:
				return {
					icon: "AlertCircle",
					color: "red11",
					text: "Action Required",
					shimmer: false,
				};
			case TaskStatus.Snoozed:
				const text = unsnoozeDate
					? `Snoozed until ${formatTimestampRelative(unsnoozeDate)}`
					: "Snoozed until new activity";
				return { icon: "Clock", color: "blue11", text, shimmer: false };
			case TaskStatus.Completed:
				return {
					icon: "Check",
					color: "green11",
					text: "Completed",
					shimmer: false,
				};
			default:
				unreachable(status);
		}
	}
};

const StatusView: React.FC<StatusViewProps> = ({
	status,
	runningJobs,
	unsnoozeDate,
}) => {
	const { icon, color, text, shimmer } = getStatusConfig(
		status,
		runningJobs,
		unsnoozeDate,
	);

	return (
		<Flex align="center" gap="xs" grow={0}>
			<Icon name={icon} size="xs" color={color} shrink={0} />
			<Text
				size="sm"
				color="sand11"
				shimmer={shimmer}
				truncate={3}
				style={{ wordBreak: "break-word" }}
			>
				{text}
			</Text>
		</Flex>
	);
};

type TaskCardProps = {
	taskId: TaskId;
	selected: boolean;
	onClick: () => void;
	/**
	 * Set while staff have picked a sort, so the date on the card is the one the
	 * list is ordered by rather than `lastActivityTimestamp`.
	 */
	sortDate: { label: string; at: Date } | undefined;
};

const TaskCard = forwardRef<HTMLDivElement, TaskCardProps>(
	({ taskId, selected, onClick, sortDate }, ref) => {
		const task = useEntity("task", taskId);
		const patient = useEntity("patient", task?.patient);
		const assignee = useEntity("user", task?.assignedTo);
		const specialty = useEntity("specialty", task?.specialtyId);

		const theme = useTheme();
		const hoverStyles = useStyles({
			userSelect: "none",
			"&:hover": {
				backgroundColor: selected ? undefined : theme.colors.sandA2,
			},
		});

		if (!task) return null;

		// Format the task display based on type
		const getTaskDisplay = () => {
			if (task.taskType === "Referral") {
				const patientName = patient?.name || "Unknown Patient";
				const specialtyText = specialty
					? `${specialty.name} Referral`
					: "Select a specialty";
				return `${patientName} → ${specialtyText}`;
			}
			return task.title;
		};

		// Priority color mapping
		const getPriorityColor = (
			priority: "Urgent" | "High" | "Medium" | undefined,
		) => {
			switch (priority) {
				case "Urgent":
					return "red11";
				case "High":
					return "amber11";
				case "Medium":
					return "blue11";
				default:
					return "sand9";
			}
		};

		return (
			<Flex
				ref={ref}
				column
				gap="xs"
				borderRadius="md"
				padding="sm"
				background={selected ? "sand4" : undefined}
				className={hoverStyles}
				onClick={onClick}
				role="button"
			>
				{task.priority && (
					<Text
						size="xs"
						color={getPriorityColor(task.priority)}
						weight="bold"
						style={{
							textTransform: "uppercase",
						}}
					>
						{task.priority.toUpperCase()}
					</Text>
				)}
				<Flex flex justify="space-between" align="center">
					<StatusView
						status={task.status}
						runningJobs={task.runningJobs}
						unsnoozeDate={task.unsnoozeDate}
					/>
					<Text size="sm" color="sand11">
						{sortDate
							? `${sortDate.label} ${formatTimestampRelative(sortDate.at)}`
							: `Updated ${formatTimestampRelative(task.lastActivityTimestamp)}`}
					</Text>
				</Flex>
				<Text size="sm" weight="medium" color="sand12">
					{getTaskDisplay()}
				</Text>
				{task.taskType !== "Referral" && (
					<Text size="sm" color="sand11">
						{patient?.name || "Unknown Patient"}
					</Text>
				)}
				{(assignee || task.assigneeType === "agent") && (
					<Flex align="center" gap="xs">
						{assignee ? (
							<>
								<Avatar
									image={assignee.imageUrl || undefined}
									name={assignee.name}
									size="xs"
									showIcon
								/>
								<Text size="sm" color="sand11">
									{assignee.name}
								</Text>
							</>
						) : (
							<>
								<Flex align="center" justify="center">
									<Icon name="CircleUser" color="sand11" size="xs" />
								</Flex>
								<Text size="sm" color="sand11">
									Unassigned
								</Text>
							</>
						)}
						{task.assigneeType === "agent" && (
							<Icon name="Bot" color="sand11" size="xs" />
						)}
					</Flex>
				)}
				<Flex align="center" gap="xs">
					<Icon name="Building" size="xs" color="sand11" />
					<Text size="sm" color="sand11">
						{task.department || "Unknown Department"}
					</Text>
				</Flex>
				<Flex align="center" gap="xs">
					<Icon name="Calendar" size="xs" color="sand11" />
					<Text size="sm" color="sand11">
						Created {formatTimestampRelative(task.createdAt)}
					</Text>
				</Flex>
			</Flex>
		);
	},
);

const SORT_MODE_LABELS: Record<TaskSortMode, string> = {
	recommended: "Recommended",
	longestIdle: "Longest idle",
	oldestReferral: "Oldest referral",
};

type TaskSortMenuProps = {
	/** What this view's recommended order does, shown under its option. */
	recommendedDescription: string;
	sortMode: TaskSortMode;
	onChange: (sortMode: TaskSortMode) => void;
};

/**
 * Sits under the task count rather than among the filters: it reorders the list
 * without narrowing it.
 */
function TaskSortMenu({
	recommendedDescription,
	sortMode,
	onChange,
}: TaskSortMenuProps) {
	return (
		<Flex align="center" gap="xxs">
			<Text size="sm" color="sand11">
				Sort by
			</Text>
			<MenuTrigger>
				<Button
					variant="ghost"
					size="sm"
					aria-label={`Sort by ${SORT_MODE_LABELS[sortMode]}`}
				>
					<Flex align="center" gap="xxs">
						<Text size="sm" color="sand12" weight="medium">
							{SORT_MODE_LABELS[sortMode]}
						</Text>
						<Icon name="ChevronDown" size="xs" color="sand11" />
					</Flex>
				</Button>
				<Popover placement="bottom start">
					<Menu aria-label="Sort tasks">
						{keys(SORT_MODE_LABELS).map((mode) => (
							<MenuItem key={mode} onAction={() => onChange(mode)}>
								<Flex column flex gap="xxs">
									<Text size="sm" weight="medium">
										{SORT_MODE_LABELS[mode]}
									</Text>
									{mode === "recommended" && (
										<Text size="xs" color="sand11">
											{recommendedDescription}
										</Text>
									)}
								</Flex>
								{sortMode === mode && (
									<Icon name="Check" color="accent9" size="sm" />
								)}
							</MenuItem>
						))}
					</Menu>
				</Popover>
			</MenuTrigger>
		</Flex>
	);
}

const UserProfile: React.FC<User> = (user: User) => {
	const theme = useTheme();
	const buttonStyles = useStyles({
		all: "unset",
		cursor: "pointer",
		borderRadius: theme.borderRadiuses.md,
		padding: theme.spacing.xs,
		display: "block",
		"&:hover": {
			backgroundColor: theme.colors.sandA2,
		},
		"&:focus": {
			outline: `2px solid ${theme.colors.accent7}`,
			outlineOffset: "2px",
		},
	});

	return (
		<AriaButton className={buttonStyles}>
			<Flex column gap="md">
				<Flex align="center" gap="md">
					<Avatar
						image={user.imageUrl || undefined}
						name={user.name}
						showIcon
					/>
					<Flex column flex={1}>
						<Text size="sm" weight="medium" color="sand12">
							{user.name}
						</Text>
						<Text size="sm" color="sand11">
							{user.email}
						</Text>
					</Flex>
				</Flex>
			</Flex>
		</AriaButton>
	);
};

type LeftPaneProps = {};

export const LeftPane: React.FC<LeftPaneProps> = () => {
	const [_match, params] = useRoute(RoutePattern.taskDetail);
	const taskId = params?.taskId ? tag<TaskId>(params.taskId) : undefined;

	const [_location, navigate] = useLocation();
	const apiClient = useApiClient();
	const user = useLoggedInUser();
	const db = useDatabase();
	const [developerMode] = useExperiment(Experiment.DeveloperMode);
	const [startedPullingReferrals, setStartedPullingReferrals] = useState(false);
	const [hasScrolledIntoView, setHasScrolledIntoView] = useState(false);
	const [isLoadingTasks, setIsLoadingTasks] = useState(true);
	const [statusFilter, setStatusFilter] = useState<TaskListFilter>(
		TaskListFilter.ActionRequired,
	);
	const [taskAssigneeFilter, setTaskAssigneeFilter] = useState<TaskAssignee>(
		TaskAssignee.MyTasks,
	);
	// Session-only: the recommended order stays the default each time staff
	// return.
	const [sortMode, setSortMode] = useState<TaskSortMode>("recommended");
	const [searchText, setSearchText] = useState("");
	const [searchResultIds, setSearchResultIds] = useState<string[] | null>(null);

	const searchTasks = useCallback(
		async (query: string) => {
			if (!query.trim()) {
				setSearchResultIds(null);
				return;
			}
			try {
				const result = await apiClient.tools.searchTasks({
					patientName: query,
				});
				setSearchResultIds(result.taskIds);
			} catch (error) {
				console.error("Search failed:", error);
				setSearchResultIds(null);
			}
		},
		[apiClient],
	);

	const handleSearchKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			if (e.key === "Enter") {
				void searchTasks(searchText);
			}
		},
		[searchTasks, searchText],
	);

	useEffect(() => {
		if (!searchText.trim()) {
			setSearchResultIds(null);
		}
	}, [searchText]);

	// Wait for local DB ready + first remote pull before treating an empty
	// list as "no matching tasks" (avoids flashing that message on load).
	useEffect(() => {
		let cancelled = false;

		void (async () => {
			try {
				await db.ready;
				const pull = db.pullFromRemote();
				if (pull) {
					await pull;
				}
			} finally {
				if (!cancelled) {
					setIsLoadingTasks(false);
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [db]);

	// Get all departments for resolving user's department and selection
	const allDepartments = useQuery("department", (q) => q, []);
	// Department selection state - get options from database
	const departmentOptions = getDepartmentOptions(allDepartments);

	// Resolve user's current department name from their departmentId
	const userDepartment = allDepartments.find(
		(dept) => dept.id === user?.departmentId,
	);

	const [selectedDepartment, setSelectedDepartment] =
		useState<string>("All Departments");

	// Update selectedDepartment when userDepartment changes (handles race condition on refresh)
	useEffect(() => {
		if (userDepartment?.name) {
			setSelectedDepartment(userDepartment.name);
		}
	}, [userDepartment?.name]);

	// Update local state when user preferences change
	useEffect(() => {
		if (user?.statusFilter) {
			setStatusFilter(user.statusFilter);
		}
	}, [user?.statusFilter]);

	useEffect(() => {
		if (user?.taskAssigneeFilter) {
			setTaskAssigneeFilter(user.taskAssigneeFilter);
		}
	}, [user?.taskAssigneeFilter]);

	// Combined function to update both local state and database
	const updateUserDepartment = useTransaction(
		(tx, newDepartmentName: string) => {
			if (user) {
				const currentUser = tx.get("user", user.id);
				if (currentUser) {
					// Find department ID by name
					const targetDepartment = allDepartments.find(
						(dept) => dept.name === newDepartmentName,
					);
					tx.set("user", {
						...currentUser,
						departmentId: targetDepartment?.id,
					});
				}
			}
		},
	);

	const handleDepartmentChange = (newDepartment: string | undefined) => {
		if (!newDepartment) return;
		setSelectedDepartment(newDepartment);
		updateUserDepartment(newDepartment);
	};

	// Combined function to update task view preference in database
	const updateUserTaskAssigneeFilterPreference = useTransaction(
		(tx, taskAssigneeFilter: TaskAssignee) => {
			if (user) {
				const currentUser = tx.get("user", user.id);
				if (currentUser) {
					tx.set("user", {
						...currentUser,
						taskAssigneeFilter,
					});
				}
			}
		},
	);

	// Combined function to update status filter preference in database
	const updateUserStatusFilter = useTransaction(
		(tx, statusFilter: TaskListFilter) => {
			if (user) {
				const currentUser = tx.get("user", user.id);
				if (currentUser) {
					tx.set("user", {
						...currentUser,
						statusFilter,
					});
				}
			}
		},
	);

	const handleTaskAssigneeFilterChange = (filter: TaskAssignee) => {
		setTaskAssigneeFilter(filter);
		updateUserTaskAssigneeFilterPreference(filter);
	};

	const handleStatusFilterChange = (filter: TaskListFilter) => {
		setStatusFilter(filter);
		updateUserStatusFilter(filter);
	};

	const goToTask = (taskId: TaskId) => {
		navigate(Routes.taskDetail(taskId));
	};

	const createTask = useTransaction(
		(tx, taskId: TaskId, department: string) => {
			const patientId = tag<PatientId>(crypto.randomUUID());

			const newPatient: Patient = {
				id: patientId,
				name: "",
				phoneNumber: undefined,
				responsiblePartyName: undefined,
				dateOfBirth: undefined,
				address: undefined,
				primaryLanguage: undefined,
				insurance: [],
				primaryProvider: undefined,
				consentRequestStatus: ConsentStatus.Unknown,
				pcpValidatedAt: undefined,
				spruceContactId: undefined,
			};
			tx.set("patient", newPatient);

			const newTask: Task = {
				id: taskId,
				title: "",
				currentStep: "",
				status: TaskStatus.ActionRequired,
				department,
				taskType: "Referral",
				priority: undefined,
				linkedTaskParameters: {
					type: "legacy" as const,
					referralDate: new Date().toISOString(),
					specialty: undefined,
					patientName: "",
					patientDob: "",
					diagnosisCodes: undefined,
					context: undefined,
					ehrStatus: undefined,
				},
				specialtyId: undefined,
				patient: patientId,
				patientName: newPatient.name,
				unsnoozeDate: undefined,
				activityLog: [],
				createdBy: user?.id,
				assignedTo: user?.id,
				assigneeType: "user" as const,
				referralId: undefined,
				lastActivityTimestamp: new Date(),
				runningJobs: [],
				createdAt: new Date(),
			};
			tx.set("task", newTask);
		},
	);

	const handleNewTask = () => {
		const taskId = tag<TaskId>(crypto.randomUUID());
		createTask(taskId, selectedDepartment);
		goToTask(taskId);
	};

	const handleClearAllState = async () => {
		if (
			confirm("This will delete all data and reload the page. Are you sure?")
		) {
			await db.clear();
			window.location.reload();
		}
	};

	const handleExportToJson = () => {
		const idb = window.indexedDB.open("saffron-health", 1);

		idb.onsuccess = (event) => {
			const database = (event.target as IDBOpenDBRequest).result;
			const transaction = database.transaction(["tupledb"], "readonly");
			const store = transaction.objectStore("tupledb");

			const getAllRequest = store.getAll();
			const getAllKeysRequest = store.getAllKeys();

			getAllRequest.onsuccess = () => {
				getAllKeysRequest.onsuccess = () => {
					const values = getAllRequest.result;
					const keys = getAllKeysRequest.result;

					const data = keys.map((key: unknown, index: number) => ({
						key: key,
						value: values[index],
					}));

					const jsonData = JSON.stringify(data, null, 2);
					const blob = new Blob([jsonData], { type: "application/json" });

					const url = URL.createObjectURL(blob);
					const a = document.createElement("a");
					a.href = url;
					a.download = `saffron-health-tupledb-${new Date().toISOString().split("T")[0]}.json`;
					document.body.appendChild(a);
					a.click();
					document.body.removeChild(a);
					URL.revokeObjectURL(url);
				};
			};
		};
	};

	// Get all tasks for search indexing (with optional status filter)
	const allTasks = useQuery(
		"task",
		(q) => {
			let query = q;
			// Undefined for "Any Status". Both options under Action Required narrow
			// to it here; `enrichedTasks` then keeps one side of the sent line.
			const status = statusForTaskListFilter(statusFilter);
			if (status !== undefined) {
				query = query.where("status", "=", status);
			}
			if (taskAssigneeFilter === TaskAssignee.MyTasks && user?.id) {
				query = query.where("assignedTo", "=", user.id);
			} else if (taskAssigneeFilter === TaskAssignee.AgentTasks) {
				query = query.where("assigneeType", "=", "agent");
			} else if (taskAssigneeFilter === TaskAssignee.UnassignedTasks) {
				query = query.where("assignedTo", "=", null);
			}
			if (selectedDepartment !== "All Departments") {
				query = query.where("department", "=", selectedDepartment);
			}
			return query;
		},
		[taskAssigneeFilter, statusFilter, user?.id, selectedDepartment],
	);

	// Get all users for enriching task data
	const allUsers = useQuery("user", (q) => q, []);

	// Tasks whose referral has been faxed to a specialist, which the two options
	// under Action Required split on — one keeps them, the other drops them.
	// Queried by `activityType` rather than `payload.type` because `QueryBuilder`
	// cannot address nested attributes; the column is populated on every row.
	const referralSentItems = useQuery(
		"activityLogItem",
		(q) => q.where("activityType", "=", ActivityType.ReferralSent),
		[],
	);
	const referralSentTaskIds = useMemo(
		() => new Set(referralSentItems.map((item) => item.taskId)),
		[referralSentItems],
	);

	// Views without the sort menu always use their recommended order, whatever
	// was picked on the last view that had one.
	const activeSortMode: TaskSortMode = offersSortChoice(statusFilter)
		? sortMode
		: "recommended";

	// Every activity item, to find when each task last had activity. Queried
	// whole because whether an item counts can depend on its payload, which
	// `QueryBuilder` cannot address. Subscribed only while that sort is on: the
	// subscription re-runs on every activity write, and most sessions never pick
	// this sort.
	const isSortedByLastTouched = activeSortMode === "longestIdle";
	const allActivityLogItems = useQuery(
		"activityLogItem",
		isSortedByLastTouched ? (q) => q : undefined,
		[isSortedByLastTouched],
	);
	const lastTouched = useMemo(
		() => lastTouchedByTask(allActivityLogItems ?? []),
		[allActivityLogItems],
	);
	// The render that turns the sort on has no activity yet: `useQuery` only
	// subscribes in an effect, after paint. Sorting then would paint every task
	// in creation order with its creation date as its last activity, which reads
	// as real data. Hold the recommended order until the items arrive.
	const displayedSortMode: TaskSortMode =
		isSortedByLastTouched && allActivityLogItems === undefined
			? "recommended"
			: activeSortMode;

	// Create enriched tasks with related data for search
	const enrichedTasks = useMemo(() => {
		let tasks = allTasks.map((task) => {
			const assignedToUser = allUsers.find((u) => u.id === task.assignedTo);
			const createdByUser = allUsers.find((u) => u.id === task.createdBy);

			return {
				...task,
				assignedToName: assignedToUser?.name,
				createdByName: createdByUser?.name,
			};
		});

		// Additional filtering for UnassignedTasks to exclude agent tasks
		if (taskAssigneeFilter === TaskAssignee.UnassignedTasks) {
			tasks = tasks.filter((task) => task.assigneeType !== "agent");
		}

		// Only the half the store query cannot express. Re-checking status here
		// would filter against a store result that is one render stale after a
		// filter change, briefly emptying the list.
		const sentReferrals = sentReferralFilterFor(statusFilter);
		if (sentReferrals !== "any") {
			const keepSent = sentReferrals === "sent-only";
			tasks = tasks.filter(
				(task) => referralSentTaskIds.has(task.id) === keepSent,
			);
		}

		return tasks;
	}, [
		allTasks,
		allUsers,
		taskAssigneeFilter,
		statusFilter,
		referralSentTaskIds,
	]);

	// Filter tasks by search results from API
	const unsortedTasksToShow = useMemo(() => {
		if (searchResultIds === null) {
			return enrichedTasks;
		}
		const idSet = new Set(searchResultIds);
		return enrichedTasks.filter((task) => idSet.has(task.id));
	}, [enrichedTasks, searchResultIds]);

	// The views staff pick work up from order by the coordinator's rule: unsent
	// referrals first, then priority, then oldest referral. The rest stay on most
	// recently active first. Neither can move into the store query -- `order()`
	// takes top-level attributes only, and neither the referral date (nested in
	// `linkedTaskParameters`) nor send status (an activity log join) is one.
	const tasks = useMemo(() => {
		switch (displayedSortMode) {
			case "longestIdle":
				return sortTasksByLastTouched(unsortedTasksToShow, lastTouched);
			case "oldestReferral":
				return sortTasksByReferralDate(unsortedTasksToShow);
			case "recommended":
				return sortsByWorkQueue(statusFilter)
					? sortTasksForLeftPane(unsortedTasksToShow, referralSentTaskIds)
					: sortBy(unsortedTasksToShow, [
							(task) => task.lastActivityTimestamp.getTime(),
							"desc",
						]);
			default:
				return unreachable(displayedSortMode);
		}
	}, [
		unsortedTasksToShow,
		referralSentTaskIds,
		statusFilter,
		displayedSortMode,
		lastTouched,
	]);

	// The date each card shows beside its status: the one the list is ordered
	// by, or undefined to keep the card's usual "Updated".
	const sortDateFor = (
		task: (typeof tasks)[number],
	): { label: string; at: Date } | undefined => {
		switch (displayedSortMode) {
			case "longestIdle":
				return { label: "Last activity", at: lastTouchedAt(task, lastTouched) };
			case "oldestReferral":
				return { label: "Referred", at: referralSortDate(task) };
			case "recommended":
				return undefined;
			default:
				return unreachable(displayedSortMode);
		}
	};

	// Helper variables for empty state
	const hasSearchWithFilters =
		searchText &&
		(statusFilter !== TaskListFilter.AnyStatus ||
			taskAssigneeFilter !== TaskAssignee.EveryoneTasks ||
			selectedDepartment !== "All Departments");

	// Create select options with counts
	const selectOptions = [
		{
			value: TaskListFilter.AnyStatus,
			label: `Any Status`,
		},
		{
			value: TaskListFilter.ActionRequired,
			label: `Action Required`,
		},
		{
			value: TaskListFilter.ReferralNotSent,
			label: `Referral Not Sent`,
			indent: true,
		},
		// The label matches the value today, but they are not the same thing: the
		// value is persisted per user. Reword this label freely; see the note on
		// TaskListFilter before touching the value it points at.
		{
			value: TaskListFilter.ReferralSent,
			label: `Referral Sent`,
			indent: true,
		},
		{ value: TaskListFilter.Snoozed, label: `Snoozed` },
		{ value: TaskListFilter.Completed, label: `Completed` },
	];

	const handlePullReferrals = async () => {
		setStartedPullingReferrals(true);
		try {
			const dept = allDepartments.find((d) => d.name === selectedDepartment);
			if (!dept?.ehrVendorName) {
				throw new Error(
					`Department ${selectedDepartment} has no EHR vendor configured`,
				);
			}
			await apiClient.tools.executeBrowserAgentJob({
				browserAgentJobTask: JobType.PullOpenReferrals,
				parameters: {
					vendorName: dept.ehrVendorName,
				},
			});
		} catch (error) {
			console.error("Failed to pull referrals from EHR:", error);
			setStartedPullingReferrals(false);
		}
	};

	const department = allDepartments.find(
		(dept) => dept.name === selectedDepartment,
	);

	const prevPullJobStatusRef = useRef(department?.pullJobStatus);

	useEffect(() => {
		if (
			prevPullJobStatusRef.current === "running" &&
			(department?.pullJobStatus === "finished" ||
				department?.pullJobStatus === "error")
		) {
			toast.success(
				<Text size="md">
					Referrals synced for <Text weight="semibold">{department?.name}</Text>
				</Text>,
			);
			setStartedPullingReferrals(false);
		}
		prevPullJobStatusRef.current = department?.pullJobStatus;
	}, [department?.pullJobStatus, department?.name]);

	const isSyncing =
		department?.pullJobStatus === "running" || startedPullingReferrals;

	// Sync status and manual pull are per-department, so they have nothing to
	// show when the task list spans every department.
	const showDepartmentSync = selectedDepartment !== "All Departments";

	return (
		<Flex
			column
			background="sand1"
			borderRightWidth="md"
			borderColor="sandA3"
			flex
			style={{ position: "relative" }}
		>
			{/* Scrollable content area */}
			<Flex
				column
				padding="xl"
				// gap="xxxs"
				overflow="y"
				flex="1 1 0"
			>
				{user && (
					<WithDialog
						title="User Profile"
						dialog={
							<Flex column gap="xl">
								<Flex align="center" gap="md">
									<Avatar
										image={user.imageUrl || undefined}
										name={user.name}
										showIcon
									/>
									<Flex column>
										<Text size="sm" weight="medium" color="sand12">
											{user.name}
										</Text>
										<Text size="sm" color="sand11">
											{user.email}
										</Text>
									</Flex>
								</Flex>

								<Flex column gap="md">
									<Text size="sm" weight="medium" light>
										Experiments
									</Text>
									{keys(EXPERIMENTS).map((experimentKey) => (
										<ExperimentToggle
											key={experimentKey}
											experiment={experimentKey}
										/>
									))}
								</Flex>

								<Flex gap="md" justify="flex-end">
									<Button
										onPress={() => {
											void signOutAndReload();
										}}
										internal_slot="close"
										variant="outline"
									>
										Sign Out
									</Button>
								</Flex>
							</Flex>
						}
					>
						<UserProfile {...user} />
					</WithDialog>
				)}

				{/* Developer Mode Actions */}
				{developerMode && (
					<>
						<Spacer height="sm" />
						<Flex column gap="md">
							<Text size="sm" weight="medium" light>
								Developer
							</Text>
							<Flex column gap="xs">
								<Button variant="ghost" size="sm" onPress={handleNewTask}>
									<Flex align="center" gap="xs">
										<Icon name="Plus" size="sm" />
										<Text size="sm">New Task</Text>
									</Flex>
								</Button>
								<Button variant="ghost" size="sm" onPress={handleExportToJson}>
									<Flex align="center" gap="xs">
										<Icon name="Download" size="sm" />
										<Text size="sm">Export Data</Text>
									</Flex>
								</Button>
								<Button variant="ghost" size="sm" onPress={handleClearAllState}>
									<Flex align="center" gap="xs">
										<Icon name="Trash2" size="sm" color="red11" />
										<Text size="sm" color="red11">
											Clear All Data
										</Text>
									</Flex>
								</Button>
								<Button
									variant="ghost"
									size="sm"
									onPress={() => navigate("/uikit")}
								>
									<Flex align="center" gap="xs">
										<Icon name="BookMarked" size="sm" />
										<Text size="sm">UI Kit</Text>
									</Flex>
								</Button>
								<Button
									variant="ghost"
									size="sm"
									onPress={() => navigate(Routes.error)}
								>
									<Flex align="center" gap="xs">
										<Icon name="AlertCircle" size="sm" />
										<Text size="sm">Error Page</Text>
									</Flex>
								</Button>
							</Flex>
						</Flex>
					</>
				)}

				<Spacer height="sm" />
				<Flex borderBottomWidth="md" borderColor="sandA3" />
				<Spacer height="sm" />

				<TrainingGuideCard />

				{/* Search and Filters Section */}
				<Flex column gap="md">
					<Spacer height="xxs" />
					<Text size="sm" weight="medium" light>
						Filters
					</Text>

					{/* Department Selection */}
					<Select
						size="sm"
						aria-label="Department filter"
						options={departmentOptions.map((dept) => ({
							value: dept,
							label: dept,
						}))}
						value={selectedDepartment}
						onSelectionChange={handleDepartmentChange}
					/>

					<Select
						size="sm"
						aria-label="Status filter"
						options={selectOptions}
						value={statusFilter}
						onSelectionChange={(value) =>
							handleStatusFilterChange(value as TaskListFilter)
						}
					/>

					<Select
						size="sm"
						aria-label="Task view mode"
						options={[
							{
								value: TaskAssignee.MyTasks,
								label: "Assigned to me",
							},
							{
								value: TaskAssignee.EveryoneTasks,
								label: "Assigned to anyone",
							},
							{
								value: TaskAssignee.AgentTasks,
								label: "Assigned to agent",
							},
							{
								value: TaskAssignee.UnassignedTasks,
								label: "Unassigned",
							},
						]}
						value={taskAssigneeFilter}
						onSelectionChange={(value) =>
							handleTaskAssigneeFilterChange(value as TaskAssignee)
						}
					/>
				</Flex>

				{/* Search Bar */}
				<Spacer height="lg" />
				<Flex column gap="md">
					{/* Task count, plus the per-department sync status and manual pull */}
					<Flex align="center" grow shrink justify="space-between">
						<Text size="sm" color="sand11" flex={1}>
							{/* Hold the count back until the store has settled, otherwise
							    the first paint reads "0 tasks" over the loading shimmer. */}
							{!isLoadingTasks && (
								<>
									{tasks.length} task{tasks.length === 1 ? "" : "s"}
									{showDepartmentSync && !isSyncing && (
										<>
											{" "}
											{department?.pullJobErrorMessage ? (
												<Text color="red11" size="sm" flex={1} truncate>
													• Error while syncing
												</Text>
											) : department?.pullJobFinishedTime ? (
												<>
													• Last synced{" "}
													{formatTimestampRelative(
														department.pullJobFinishedTime,
													)}
												</>
											) : (
												<>• Not synced yet</>
											)}
										</>
									)}
								</>
							)}
						</Text>
						{showDepartmentSync &&
							(isSyncing ? (
								<Text size="sm" color="accent11" weight="medium">
									Syncing...
								</Text>
							) : (
								<WithDialog
									title="Sync Tasks"
									dialog={
										<Flex column gap="lg">
											<Text size="md" color="sand11">
												{`This will sync new referrals for the ${department?.ehrVendorName} EHR. Are you sure you want to continue?`}
											</Text>

											<Flex gap="sm" justify="flex-end">
												<Button
													onPress={handlePullReferrals}
													internal_slot="close"
													size="md"
												>
													Sync Now
												</Button>
											</Flex>
										</Flex>
									}
								>
									<Button variant="ghost" size="sm">
										Sync now
									</Button>
								</WithDialog>
							))}
					</Flex>
					{/* Its own line: the count row already holds the sync status and
					    "Sync now" whenever a department is selected. */}
					{offersSortChoice(statusFilter) && (
						<TaskSortMenu
							recommendedDescription={recommendedSortDescription(statusFilter)}
							sortMode={sortMode}
							onChange={setSortMode}
						/>
					)}
					<Flex gap="sm" align="center">
						<Flex flex={1}>
							<TextField
								aria-label="Search tasks"
								placeholder="Search by patient name (press Enter)"
								value={searchText}
								onChange={(e) => setSearchText(e.target.value)}
								onKeyDown={handleSearchKeyDown}
								outlineWhenNonEmpty
								background="sand1"
								subtlePlaceholder={false}
							/>
						</Flex>
					</Flex>
				</Flex>

				<Flex column gap="md">
					{/* Task View Toggle */}
					<Flex
						column
						gap="md"
						flex={1}
						style={{ paddingTop: 16, overflowY: "auto" }}
					>
						{tasks.length === 0 ? (
							<Flex
								column
								gap="lg"
								align="center"
								justify="center"
								padding="xl"
								style={{ minHeight: 200 }}
							>
								{isLoadingTasks ? (
									<Text size="sm" color="sand11" align="center" shimmer>
										Loading tasks...
									</Text>
								) : (
									<>
										<Icon name="Inbox" size="lg" color="sand9" />
										<Text size="sm" color="sand11" align="center">
											{hasSearchWithFilters
												? `No tasks match "${searchText}" in these filters`
												: searchText
													? `No tasks match "${searchText}"`
													: "No tasks match these filters"}
										</Text>
										{hasSearchWithFilters && (
											<Button
												variant="ghost"
												size="sm"
												onPress={() => {
													handleStatusFilterChange(TaskListFilter.AnyStatus);
													handleTaskAssigneeFilterChange(
														TaskAssignee.EveryoneTasks,
													);
													handleDepartmentChange("All Departments");
												}}
											>
												Search all tasks
											</Button>
										)}
									</>
								)}
							</Flex>
						) : (
							<>
								{tasks.map((task) => {
									return (
										<TaskCard
											key={task.id}
											ref={(element) => {
												if (task.id === taskId && !hasScrolledIntoView) {
													element?.scrollIntoView({
														behavior: "instant",
														block: "center",
													});
													setHasScrolledIntoView(true);
												}
											}}
											taskId={task.id}
											selected={taskId === task.id}
											onClick={() => goToTask(task.id)}
											sortDate={sortDateFor(task)}
										/>
									);
								})}
								{hasSearchWithFilters && (
									<Flex column align="center" gap="sm" padding="lg">
										<Text size="sm" color="sand11">
											Don't see what you were looking for?
										</Text>
										<Button
											variant="ghost"
											size="sm"
											onPress={() => {
												handleStatusFilterChange(TaskListFilter.AnyStatus);
												handleTaskAssigneeFilterChange(
													TaskAssignee.EveryoneTasks,
												);
												handleDepartmentChange("All Departments");
											}}
										>
											Search all tasks
										</Button>
									</Flex>
								)}
							</>
						)}
					</Flex>
				</Flex>
			</Flex>
		</Flex>
	);
};
